import { useState } from "react";
import Layout from "@/components/Layout";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Calendar, Clock, MapPin, Users, ChevronRight, Filter } from "lucide-react";
import { format, parseISO, addDays } from "date-fns";

interface Event {
  id: number;
  title: string;
  description: string;
  date: string;
  time: string;
  location: string;
  type: "webinar" | "conference" | "workshop" | "meetup";
  attendees: number;
  isRegistered: boolean;
  imageUrl?: string;
}

// Sample events data
const eventsData: Event[] = [
  {
    id: 1,
    title: "Understanding Deepfakes Workshop",
    description: "Learn how to identify AI-generated images and videos with our expert panel of researchers and technologists.",
    date: "2025-06-15",
    time: "14:00-16:00",
    location: "Online",
    type: "workshop",
    attendees: 156,
    isRegistered: false,
  },
  {
    id: 2,
    title: "Global Truth Conference 2025",
    description: "Join the biggest gathering of fact-checking organizations, researchers, and journalists fighting misinformation worldwide.",
    date: "2025-07-10",
    time: "09:00-18:00",
    location: "New York, United States",
    type: "conference",
    attendees: 478,
    isRegistered: false,
  },
  {
    id: 3,
    title: "Misinformation in Elections: Challenges & Solutions",
    description: "A discussion with election officials and platform representatives about protecting democratic processes from disinformation.",
    date: format(addDays(new Date(), 3), "yyyy-MM-dd"),
    time: "15:30-17:00",
    location: "Online",
    type: "webinar",
    attendees: 289,
    isRegistered: true,
  },
  {
    id: 4,
    title: "News Literacy Training for Educators",
    description: "A workshop designed to help teachers incorporate media literacy skills into their curriculum.",
    date: format(addDays(new Date(), 7), "yyyy-MM-dd"),
    time: "10:00-15:00",
    location: "Chicago, United States",
    type: "workshop",
    attendees: 65,
    isRegistered: false,
  },
  {
    id: 5,
    title: "Truth Nexus Community Meetup",
    description: "Connect with others in the fight against misinformation. Share strategies, tools, and experiences in this informal gathering.",
    date: format(addDays(new Date(), 14), "yyyy-MM-dd"),
    time: "18:00-20:00",
    location: "Multiple Locations",
    type: "meetup",
    attendees: 103,
    isRegistered: false,
  },
];

const Events = () => {
  const [events, setEvents] = useState<Event[]>(eventsData);
  const [selectedEvent, setSelectedEvent] = useState<Event | null>(null);
  const [activeView, setActiveView] = useState<"all" | "registered" | "details">("all");
  const [filter, setFilter] = useState<"all" | "webinar" | "conference" | "workshop" | "meetup">("all");

  const handleRegister = (eventId: number) => {
    setEvents(
      events.map((event) =>
        event.id === eventId ? { ...event, isRegistered: true } : event
      )
    );
  };

  const handleUnregister = (eventId: number) => {
    setEvents(
      events.map((event) =>
        event.id === eventId ? { ...event, isRegistered: false } : event
      )
    );
  };

  const handleViewDetails = (event: Event) => {
    setSelectedEvent(event);
    setActiveView("details");
  };

  const filteredEvents = events.filter((event) => {
    if (activeView === "registered") return event.isRegistered;
    if (filter !== "all") return event.type === filter;
    return true;
  });

  const renderDateBadge = (dateStr: string) => {
    const eventDate = parseISO(dateStr);
    const now = new Date();
    const diffDays = Math.floor((eventDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
    
    if (diffDays < 0) {
      return <Badge variant="outline" className="bg-gray-700/50">Past</Badge>;
    } else if (diffDays < 7) {
      return <Badge className="bg-green-600">Upcoming</Badge>;
    } else if (diffDays < 30) {
      return <Badge variant="outline" className="border-truth text-truth">This Month</Badge>;
    } else {
      return <Badge variant="outline">Future</Badge>;
    }
  };

  return (
    <div className="min-h-screen flex flex-col items-center bg-gray-900 py-6 px-2 sm:px-4">
      <div className="w-full max-w-2xl space-y-8">
        <div className="text-center">
          <h1 className="text-2xl sm:text-3xl font-bold text-truth">Events</h1>
          <p className="mt-2 text-gray-400">Join upcoming events, webinars, and workshops</p>
        </div>

        <Tabs defaultValue="all" value={activeView} onValueChange={(value) => setActiveView(value as "all" | "registered" | "details")}>
          <div className="flex justify-between items-center">
            <TabsList>
              <TabsTrigger value="all">All Events</TabsTrigger>
              <TabsTrigger value="registered">My Events</TabsTrigger>
            </TabsList>
            
            {activeView !== "details" && (
              <div className="flex items-center gap-2">
                <Filter className="h-4 w-4 text-gray-400" />
                <select 
                  className="bg-black/20 text-sm border border-gray-700 rounded-md py-1 px-2"
                  value={filter}
                  onChange={(e) => setFilter(e.target.value as any)}
                >
                  <option value="all">All Types</option>
                  <option value="webinar">Webinars</option>
                  <option value="conference">Conferences</option>
                  <option value="workshop">Workshops</option>
                  <option value="meetup">Meetups</option>
                </select>
              </div>
            )}
          </div>
          
          <TabsContent value="all" className="mt-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {filteredEvents.map((event) => (
                <EventCard
                  key={event.id}
                  event={event}
                  onRegister={handleRegister}
                  onUnregister={handleUnregister}
                  onViewDetails={handleViewDetails}
                  renderDateBadge={renderDateBadge}
                />
              ))}
            </div>
            
            {filteredEvents.length === 0 && (
              <div className="text-center py-12">
                <p className="text-gray-400">No events matching your filter.</p>
              </div>
            )}
          </TabsContent>
          
          <TabsContent value="registered" className="mt-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {filteredEvents.map((event) => (
                <EventCard
                  key={event.id}
                  event={event}
                  onRegister={handleRegister}
                  onUnregister={handleUnregister}
                  onViewDetails={handleViewDetails}
                  renderDateBadge={renderDateBadge}
                />
              ))}
            </div>
            
            {filteredEvents.length === 0 && (
              <div className="text-center py-12">
                <p className="text-gray-400">You haven't registered for any events yet.</p>
                <Button 
                  variant="outline" 
                  className="mt-4" 
                  onClick={() => { setActiveView("all"); setFilter("all"); }}
                >
                  Browse Events
                </Button>
              </div>
            )}
          </TabsContent>
          
          <TabsContent value="details" className="mt-6">
            {selectedEvent && (
              <div className="space-y-6 animate-fade-in">
                <Button 
                  variant="ghost" 
                  className="flex items-center gap-2 -ml-2" 
                  onClick={() => setActiveView(selectedEvent.isRegistered ? "registered" : "all")}
                >
                  Back to events
                </Button>
                
                <div className="bg-black/20 backdrop-blur-sm border border-gray-800 rounded-lg overflow-hidden">
                  {selectedEvent.imageUrl ? (
                    <div className="h-48 bg-gray-800 relative">
                      <img 
                        src={selectedEvent.imageUrl} 
                        alt={selectedEvent.title}
                        className="w-full h-full object-cover" 
                      />
                    </div>
                  ) : (
                    <div className="h-48 bg-gradient-to-r from-truth/30 to-purple-800/30 flex items-center justify-center">
                      <h2 className="text-2xl font-bold text-white">{selectedEvent.title}</h2>
                    </div>
                  )}
                  
                  <div className="p-6">
                    <div className="flex flex-wrap items-start justify-between gap-4 mb-4">
                      <div>
                        <h2 className="text-2xl font-bold">{selectedEvent.title}</h2>
                        <div className="flex items-center gap-2 mt-1">
                          {renderDateBadge(selectedEvent.date)}
                          <Badge variant="outline" className="capitalize">{selectedEvent.type}</Badge>
                        </div>
                      </div>
                      
                      <div>
                        {selectedEvent.isRegistered ? (
                          <Button 
                            variant="destructive" 
                            onClick={() => handleUnregister(selectedEvent.id)}
                          >
                            Cancel Registration
                          </Button>
                        ) : (
                          <Button 
                            className="bg-truth hover:bg-truth-dark" 
                            onClick={() => handleRegister(selectedEvent.id)}
                          >
                            Register Now
                          </Button>
                        )}
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-6 mb-6">
                      <div className="flex items-center gap-2">
                        <Calendar className="h-5 w-5 text-truth" />
                        <div>
                          <p className="text-sm text-gray-400">Date</p>
                          <p>{format(parseISO(selectedEvent.date), "MMMM d, yyyy")}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Clock className="h-5 w-5 text-truth" />
                        <div>
                          <p className="text-sm text-gray-400">Time</p>
                          <p>{selectedEvent.time}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <MapPin className="h-5 w-5 text-truth" />
                        <div>
                          <p className="text-sm text-gray-400">Location</p>
                          <p>{selectedEvent.location}</p>
                        </div>
                      </div>
                    </div>
                    
                    <div className="border-t border-gray-800 pt-6">
                      <h3 className="font-semibold mb-2">About This Event</h3>
                      <p className="text-gray-300">{selectedEvent.description}</p>
                    </div>
                    
                    <div className="border-t border-gray-800 pt-6 mt-6">
                      <div className="flex items-center gap-2">
                        <Users className="h-5 w-5 text-truth" />
                        <p className="text-gray-300">{selectedEvent.attendees} people registered</p>
                      </div>
                    </div>
                    
                    {selectedEvent.isRegistered && (
                      <div className="mt-6 bg-truth/10 border border-truth/30 rounded-lg p-4">
                        <h3 className="font-semibold text-truth mb-2">You're Registered!</h3>
                        <p className="text-gray-300">
                          You will receive a confirmation email with details about how to join the event.
                        </p>
                      </div>
                    )}
                  </div>
                </div>
                
                <div className="space-y-4">
                  <h3 className="font-semibold">Similar Events</h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {events
                      .filter(e => e.id !== selectedEvent.id && e.type === selectedEvent.type)
                      .slice(0, 3)
                      .map(event => (
                        <Card key={event.id} className="bg-black/20 backdrop-blur-sm border-gray-800 hover:border-gray-600 transition-all cursor-pointer" onClick={() => handleViewDetails(event)}>
                          <CardHeader className="pb-2">
                            <div className="flex justify-between">
                              {renderDateBadge(event.date)}
                              <Badge variant="outline" className="capitalize">{event.type}</Badge>
                            </div>
                            <CardTitle className="text-base mt-2">{event.title}</CardTitle>
                          </CardHeader>
                          <CardFooter className="pt-0 flex justify-between items-center">
                            <div className="text-sm text-gray-400">
                              {format(parseISO(event.date), "MMM d")} • {event.time.split('-')[0]}
                            </div>
                            <ChevronRight className="h-4 w-4" />
                          </CardFooter>
                        </Card>
                      ))}
                  </div>
                </div>
              </div>
            )}
          </TabsContent>
        </Tabs>

        <div className="mt-12 p-6 bg-black/20 backdrop-blur-sm border border-gray-800 rounded-lg">
          <h2 className="text-xl font-bold mb-4">Host Your Own Event</h2>
          <p className="text-gray-300 mb-4">
            Are you interested in hosting an event focused on fighting misinformation, promoting digital literacy, or cybersecurity?
            Partner with Truth Nexus to reach our community of professionals and enthusiasts.
          </p>
          <Button className="bg-truth hover:bg-truth-dark">Contact Us About Hosting</Button>
        </div>
      </div>
    </div>
  );
};

interface EventCardProps {
  event: Event;
  onRegister: (id: number) => void;
  onUnregister: (id: number) => void;
  onViewDetails: (event: Event) => void;
  renderDateBadge: (date: string) => React.ReactNode;
}

const EventCard = ({ event, onRegister, onUnregister, onViewDetails, renderDateBadge }: EventCardProps) => {
  return (
    <Card className="bg-black/20 backdrop-blur-sm border-gray-800">
      <CardHeader>
        <div className="flex justify-between items-start">
          <div className="space-y-1">
            <div className="flex gap-2">
              {renderDateBadge(event.date)}
              <Badge variant="outline" className="capitalize">{event.type}</Badge>
            </div>
            <CardTitle>{event.title}</CardTitle>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-2 gap-4 mb-4">
          <div className="flex items-center gap-2">
            <Calendar className="h-4 w-4 text-truth" />
            <span className="text-sm">{format(parseISO(event.date), "MMMM d, yyyy")}</span>
          </div>
          <div className="flex items-center gap-2">
            <Clock className="h-4 w-4 text-truth" />
            <span className="text-sm">{event.time}</span>
          </div>
        </div>
        <div className="flex items-center gap-2 mb-4">
          <MapPin className="h-4 w-4 text-truth" />
          <span className="text-sm">{event.location}</span>
        </div>
        <p className="text-gray-400 line-clamp-3 text-sm">{event.description}</p>
      </CardContent>
      <CardFooter className="flex flex-col sm:flex-row gap-3 border-t border-gray-800 pt-4">
        <Button 
          variant="outline" 
          className="w-full sm:w-auto"
          onClick={() => onViewDetails(event)}
        >
          View Details
        </Button>
        {event.isRegistered ? (
          <Button 
            variant="destructive" 
            className="w-full sm:w-auto"
            onClick={() => onUnregister(event.id)}
          >
            Cancel Registration
          </Button>
        ) : (
          <Button 
            className="w-full sm:w-auto bg-truth hover:bg-truth-dark"
            onClick={() => onRegister(event.id)}
          >
            Register
          </Button>
        )}
      </CardFooter>
    </Card>
  );
};

export default Events;
